import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import BadgeDisplay, { Badge } from './BadgeDisplay';
import { buildCountryDisplay } from '../utils/country';
import { getCountryNameByCode } from '../utils/countryLookup';
import { formatEarnedDate, formatPeriodMetadata } from '../utils/badgeFormatting';

interface BadgeDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  badge: Badge;
  level?: string | null;
  isUnlocked?: boolean;
  earnedAt?: string | null;
  metadata?: Record<string, any> | null;
}

const BadgeDetailModal: React.FC<BadgeDetailModalProps> = ({
  isOpen,
  onClose,
  badge,
  level,
  isUnlocked = true,
  earnedAt = null,
  metadata = null
}) => {
  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('badge-detail-modal-open');
    return () => {
      document.body.classList.remove('badge-detail-modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const earnedDateStr = formatEarnedDate(earnedAt);
  const periodStr = formatPeriodMetadata(metadata);
  const isCountryChampion = badge.name === 'Country Champion';
  const countryCode = metadata?.country_code || null;
  const countryName = metadata?.country_name || (countryCode ? getCountryNameByCode(countryCode) : null);
  const countryDisplay = countryCode || countryName ? buildCountryDisplay(countryCode, countryName) : null;
  const displayLevel = level ?? badge.level ?? null;
  const rarity = badge.rarity ? String(badge.rarity).toLowerCase() : null;
  const rarityTierMap: Record<string, string> = {
    common: 'Bronze',
    rare: 'Silver',
    epic: 'Gold',
    legendary: 'Platinum'
  };
  const normalizedTier = displayLevel
    ? String(displayLevel).trim()
    : rarity && rarityTierMap[rarity]
      ? rarityTierMap[rarity]
      : null;
  const tierKeyMatch = normalizedTier?.match(/bronze|silver|gold|platinum/i);
  const tierKey = tierKeyMatch ? tierKeyMatch[0].toLowerCase() : null;

  return createPortal(
    <div className="badge-detail-modal-overlay" onClick={onClose}>
      <div className="badge-detail-modal" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className="badge-detail-modal__close"
          onClick={onClose}
          aria-label="Close badge details"
        >
          ×
        </button>

        <div className="badge-detail-modal__header">
          <div className="badge-detail-modal__hero">
            <BadgeDisplay
              badge={badge}
              level={displayLevel}
              size="lg"
              isUnlocked={isUnlocked}
              earnedAt={earnedAt}
              metadata={metadata}
              showDescription={false}
              showMeta={false}
              interactive={false}
              className="badge-detail-modal__badge"
            />
            <div className="badge-detail-modal__pills">
              {normalizedTier && (
                <span
                  className={`badge-detail-modal__pill badge-detail-modal__pill--tier${tierKey ? ` badge-detail-modal__pill--tier-${tierKey}` : ''}`}
                >
                  {normalizedTier}
                </span>
              )}
              <span className={`badge-detail-modal__pill ${isUnlocked ? 'is-unlocked' : 'is-locked'}`}>
                {isUnlocked ? '✅ Unlocked' : '🔒 Locked'}
              </span>
            </div>
          </div>
        </div>

        <div className="badge-detail-modal__body">
          <div className="badge-detail-modal__section">
            <h3 className="badge-detail-modal__section-title">Achievement</h3>
            <p className="badge-detail-modal__description">{badge.description}</p>
          </div>

          <div className="badge-detail-modal__section">
            <h3 className="badge-detail-modal__section-title">Details</h3>
            <div className="badge-detail-modal__details-grid">
              {periodStr && (
                <div className="badge-detail-modal__detail">
                  <span className="badge-detail-modal__detail-label">Period</span>
                  <span className="badge-detail-modal__detail-value">{periodStr}</span>
                </div>
              )}
              {isCountryChampion && (countryName || countryDisplay?.title) && (
                <div className="badge-detail-modal__detail">
                  <span className="badge-detail-modal__detail-label">Country</span>
                  <span className="badge-detail-modal__detail-value">
                    {countryDisplay?.title || countryName}
                  </span>
                </div>
              )}
              {isUnlocked && earnedDateStr && (
                <div className="badge-detail-modal__detail">
                  <span className="badge-detail-modal__detail-label">Earned</span>
                  <span className="badge-detail-modal__detail-value">{earnedDateStr}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BadgeDetailModal;
